import type { DatasetImportJob, DatasetImportJobStatus } from '../types/api';

const DATASET_IMPORT_PHASE_LABELS: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  validating: 'Validating',
  fetching: 'Fetching conversations',
  uploading: 'Uploading',
  registering: 'Registering dataset',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function isDatasetImportJobTerminal(status: DatasetImportJobStatus | string | undefined): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export function isDatasetImportJobActive(status: DatasetImportJobStatus | string | undefined): boolean {
  return status === 'queued' || status === 'running';
}

const DATASET_IMPORT_POLL_STORAGE_PREFIX = 'dataset_import_poll_job:';

export function datasetImportJobStorageKey(integrationId: string): string {
  return DATASET_IMPORT_POLL_STORAGE_PREFIX + integrationId;
}

export function readPersistedDatasetImportJobId(integrationId: string): string | null {
  try {
    const raw = sessionStorage.getItem(datasetImportJobStorageKey(integrationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { jobId?: unknown };
    if (typeof parsed?.jobId === 'string' && parsed.jobId.trim()) {
      return parsed.jobId.trim();
    }
  } catch {
    // ignore
  }
  return null;
}

export function persistDatasetImportJobId(integrationId: string, jobId: string): void {
  try {
    sessionStorage.setItem(
      datasetImportJobStorageKey(integrationId),
      JSON.stringify({ v: 1, jobId: jobId.trim() })
    );
  } catch {
    // ignore
  }
}

export function clearPersistedDatasetImportJobId(integrationId: string | null | undefined): void {
  if (!integrationId) return;
  try {
    sessionStorage.removeItem(datasetImportJobStorageKey(integrationId));
  } catch {
    // ignore
  }
}

export function formatDatasetImportJobPhase(phase: string | undefined): string {
  if (!phase) return 'Importing';
  return DATASET_IMPORT_PHASE_LABELS[phase] ?? phase.replace(/_/g, ' ');
}

export function resolveDatasetImportJobProgressPercent(job: DatasetImportJob | null | undefined): number {
  const raw = job?.progress_percent;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0;
  return Math.min(100, Math.max(0, raw));
}

export type DatasetImportJobProgressVariant = 'running' | 'completed' | 'failed';

export interface DatasetImportJobProgressMetrics {
  percent: number;
  variant: DatasetImportJobProgressVariant;
  show: boolean;
  label: string;
}

function readPercent(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

export function resolveDatasetImportJobProgressMetrics(
  status: DatasetImportJobStatus | string | undefined | null,
  phase: string | undefined | null,
  progressPercent?: number | null,
  displayPercent?: number | null
): DatasetImportJobProgressMetrics {
  const normalizedStatus = (status || '').toLowerCase().trim();
  const fromApi = readPercent(progressPercent);
  const fromDisplay = readPercent(displayPercent);
  const raw = fromDisplay ?? fromApi;
  const phaseLabel = formatDatasetImportJobPhase(phase ?? undefined);

  if (normalizedStatus === 'completed') {
    return {
      percent: 100,
      variant: 'completed',
      show: true,
      label: phaseLabel || 'Completed',
    };
  }

  if (normalizedStatus === 'failed') {
    return {
      percent: raw ?? 0,
      variant: 'failed',
      show: true,
      label: phaseLabel || 'Failed',
    };
  }

  if (normalizedStatus === 'cancelled') {
    return {
      percent: raw ?? 0,
      variant: 'failed',
      show: raw != null && raw > 0,
      label: 'Cancelled',
    };
  }

  if (isDatasetImportJobActive(normalizedStatus)) {
    return {
      percent: Math.min(99, raw ?? 0),
      variant: 'running',
      show: true,
      label: phaseLabel,
    };
  }

  if (raw != null && raw > 0) {
    return {
      percent: raw,
      variant: 'running',
      show: true,
      label: phaseLabel || `${Math.round(raw)}%`,
    };
  }

  return {
    percent: 0,
    variant: 'running',
    show: false,
    label: '',
  };
}

export function buildDatasetImportSuccessMessage(job: DatasetImportJob): string {
  const meta = job.result?.dataset_meta;
  const stats = job.result?.stats;
  const label = meta?.label ?? job.dataset_label;
  let msg = 'Dataset imported successfully.';
  if (label) msg += ` Label: ${label}.`;
  if (stats) {
    msg += ` Rows: ${stats.total_rows} (train: ${stats.train_rows}, validation: ${stats.validation_rows}).`;
  } else if (job.train_rows != null || job.validation_rows != null) {
    const train = job.train_rows ?? 0;
    const validation = job.validation_rows ?? 0;
    msg += ` Rows: ${train + validation} (train: ${train}, validation: ${validation}).`;
  }
  return msg;
}

function datasetImportJobSnapshot(job: DatasetImportJob): string {
  return [
    job.status,
    job.phase ?? '',
    job.progress_percent ?? '',
    job.attempt ?? '',
    job.error ?? '',
    job.dataset_label ?? '',
    job.dataset_id ?? '',
    job.train_rows ?? '',
    job.validation_rows ?? '',
    job.updated_at ?? '',
  ].join('|');
}

/** Avoid re-rendering UI when polling returns an unchanged job payload. */
export function shouldUpdateDatasetImportJob(
  prev: DatasetImportJob | null | undefined,
  next: DatasetImportJob
): boolean {
  if (!prev) return true;
  if (prev.id !== next.id) return true;
  return datasetImportJobSnapshot(prev) !== datasetImportJobSnapshot(next);
}
